import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type { AddSpecialPricePackageFailure, FreightTerms, PriceChangeType, Product } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { AddSpecialPriceForm } from "@/screens/Pricing/components/forms/AddSpecialPriceForm"
import { useGetPriceChangeFilterOptions } from "@/screens/Pricing/hooks/useGetPriceChangeFilterOptions"
import { usePriceChange } from "@/screens/Pricing/hooks/usePriceChange"
import type { SpecialPriceSchemaFormValues } from "@/screens/Pricing/schema/SpecialPriceSchema"
import { SpecialPriceSchema } from "@/screens/Pricing/schema/SpecialPriceSchema"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface AddSpecialPriceDialogProps {
  onClose: () => void
  onSuccess: () => void
  selectedProduct: Product
  changeType?: PriceChangeType
}

const AddSpecialPriceDialog: FC<AddSpecialPriceDialogProps> = ({ onClose, onSuccess, selectedProduct, changeType }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { addSpecialPricePackage } = usePriceChange()
  const methods = useForm<SpecialPriceSchemaFormValues>({
    defaultValues: {
      customerId: null,
      shipFrom: null,
      incoterms: null,
      incotermsLocationId: null,
      freightTerms: null,
    },
    mode: "onChange",
    resolver: zodResolver(SpecialPriceSchema),
  })

  const {
    formState: { isDirty, isValid },
  } = methods

  const { filterOptions } = useGetPriceChangeFilterOptions({
    changeType,
    specificProductIds: [selectedProduct.productId],
    pause: !changeType,
  })

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState<boolean>(false)

  const confirmCancel = () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSuccess = async ({
    customerId,
    shipFrom,
    incoterms,
    incotermsLocationId,
    freightTerms,
  }: SpecialPriceSchemaFormValues) => {
    const { data, error } = await addSpecialPricePackage({
      input: {
        productId: selectedProduct.productId,
        companyId: customerId as string,
        shipFromId: shipFrom as string,
        incoterms: incoterms as string,
        incotermsLocationId: incotermsLocationId as string,
        freightTerm: freightTerms as FreightTerms,
      },
    })
    const output = data?.priceChange.addSpecialPricePackage
    const hasError = error || output?.__typename === "AddSpecialPricePackageFailure"

    if (hasError) {
      const message =
        error?.message ?? (output as AddSpecialPricePackageFailure)?.error?.message ?? "Failed to add special price."
      notify({
        type: NotificationType.Error,
        silent: false,
        message,
      })
      return
    }
    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Special price successfully added.",
    })
    onSuccess()
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth>
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>Add Special Price Package</DialogTitle>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <AddSpecialPriceForm
                customers={filterOptions?.customers}
                shipFromWarehouseAreas={filterOptions?.shipFromWarehouseAreas}
              />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel}>
              Cancel
            </Button>
            <Button disabled={!isValid || !isDirty} onClick={methods.handleSubmit(handleSuccess)}>
              Add Package
            </Button>
          </DialogActions>
        </>
      </Dialog>
    </>
  )
}

export default AddSpecialPriceDialog
